import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IAuthResponse } from '@models/response/IAuthResponse';
import { ISignUpSettings } from '@pages/signUp/ISignUpSettings';
import { IResetPasswordRequestData } from '@organisms/ResetPasswordRequestForm/IResetPasswordRequestData';
import { IResetPasswordConfirmData } from '@organisms/ResetPasswordConfirmForm/IResetPasswordConfirmData';
import { IChangeEmailConfirmData } from '@organisms/ChangeEmailConfirmForm/IChangeEmailConfirmData';
import { ISignUpConfirmData } from '@organisms/SignUpConfirmForm/ISignUpConfirmData';
import { ISignUpData } from '@organisms/SignUpForm/ISignUpData';
import { ISignInSettings } from '@srm/core/auth';

export class AuthService {
    static async login(
        username: string,
        password: string,
        rememberMe: boolean,
        deviceId: string,
        authMode: 'body' | 'cookie' = 'body',
    ): Promise<AxiosResponse<IAuthResponse>> {
        return $api.post<IAuthResponse>(`/Account/SignIn`, {
            username: username,
            password: password,
            rememberMe: rememberMe,
            deviceId: deviceId,
            authMode: authMode,
        });
    }

    static async loginByHeader(rememberMe: boolean): Promise<AxiosResponse<IAuthResponse>> {
        return $api.post<IAuthResponse>(
            `/Account/SignInByHeader`,
            { rememberMe: rememberMe },
            { skipBearerAuth: true },
        );
    }

    static async requestPasswordReset(formData: IResetPasswordRequestData): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/RequestPasswordReset`, formData);
    }

    static async confirmPasswordReset(formData: IResetPasswordConfirmData): Promise<AxiosResponse> {
        return $api.post(`/Account/ConfirmPasswordReset`, formData);
    }

    static async confirmSignUp(formData: ISignUpConfirmData): Promise<AxiosResponse<string>> {
        return $api.post(`/Account/ConfirmSignUp`, formData);
    }

    static async confirmChangeEmail(formData: IChangeEmailConfirmData): Promise<AxiosResponse<string>> {
        return $api.post(`/Account/ConfirmChangeEmail`, formData);
    }

    static async fetchSignInSettings(): Promise<AxiosResponse<ISignInSettings>> {
        // suppressErrorNotification: вызывается при каждой загрузке страницы логина —
        // если стенд обновляется, недоступность показывает каллоут, а не generic-тост.
        return $api.get(`/Account/SignInSettings`, { suppressErrorNotification: true });
    }

    static async fetchSignUpSettings(controller: AbortController): Promise<AxiosResponse<ISignUpSettings>> {
        return $api.get(`/Account/SignUpSettings`, { signal: controller.signal });
    }

    static async signUp(formData: Omit<ISignUpData, 'organization' | 'foreignOrganization'>): Promise<AxiosResponse> {
        return $api.post(`/Account/SignUp`, formData);
    }

    static async checkOrganizationExists(inn?: string, kpp?: string): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/OrganizationExists`, { inn: inn, kpp: kpp });
    }

    static async checkForeignOrganizationExists(tin?: string): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/ForeignOrganizationExists`, { tin: tin });
    }

    static async checkResetPasswordConfirmLink(actionKey?: string, login?: string): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/CheckResetPasswordConfirmLink`, { actionKey: actionKey, login: login });
    }

    static async checkSignUpConfirmLink(actionKey?: string, login?: string): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/CheckSignUpConfirmLink`, { actionKey: actionKey, login: login });
    }

    static async checkChangeEmailConfirmLink(actionKey?: string, login?: string): Promise<AxiosResponse<boolean>> {
        return $api.post(`/Account/CheckChangeEmailConfirmLink`, { actionKey: actionKey, login: login });
    }
}
